/**
 * Immediate-mode splash drawn during KAPLAY asset load (onLoading / onDraw).
 * @see https://v4000.kaplayjs.com/docs/api/ctx/onLoading/
 */

import {
    areGameAssetsLoaded,
    areGameAssetsQueued,
    getBootLoadProgress,
    noteKaplayLoadProgress,
} from "../core/assets.js";

const RAM_COUNT = 3;
const RAM_SCALE = 3;
const RAM_SPACING = 78;

let splashDrawing = true;
let peakProgress = 0;

function ramPos(index) {
    const totalW = (RAM_COUNT - 1) * RAM_SPACING;
    return center().add(
        -totalW / 2 + index * RAM_SPACING,
        12,
    );
}

function stickFill(progress, index) {
    const segment = 1 / RAM_COUNT;
    const start = index * segment;
    return Math.max(0, Math.min(1, (progress - start) / segment));
}

/** Draw black splash + 3 RAM sticks filling left→right from 0–1 progress. */
export function drawSplashLoading(progress = 0) {
    const p = Math.max(0, Math.min(1, progress));

    drawRect({
        width: width(),
        height: height(),
        pos: vec2(0, 0),
        color: rgb(0, 0, 0),
        fixed: true,
    });

    const spr = getSprite("ram_stick");
    const frameW = spr?.data?.width || 16;
    const frameH = spr?.data?.height || 16;
    const w = frameW * RAM_SCALE;
    const h = frameH * RAM_SCALE;

    for (let i = 0; i < RAM_COUNT; i++) {
        const pos = ramPos(i);
        const filled = stickFill(p, i);

        drawSprite({
            sprite: "ram_stick",
            pos,
            anchor: "center",
            scale: vec2(RAM_SCALE),
            fixed: true,
        });

        const remain = 1 - filled;
        if (remain > 0.001) {
            drawRect({
                width: w * remain,
                height: h,
                pos: pos.add(vec2(-w / 2 + w * filled, -h / 2)),
                color: rgb(0, 0, 0),
                opacity: 0.82,
                anchor: "topleft",
                fixed: true,
            });
        }
    }
}

export function isSplashDrawing() {
    return splashDrawing;
}

export function stopSplashDrawing() {
    splashDrawing = false;
}

/**
 * Hook KAPLAY onLoading + onDraw so the RAM splash covers the whole boot load
 * (including the map.lua gap when KAPLAY isn't loading).
 */
export function registerSplashLoadingHooks(onAssetsReady) {
    let finished = false;

    function finish() {
        if (finished) return;
        if (!areGameAssetsQueued()) return;
        if (!areGameAssetsLoaded() && loadProgress() < 1) return;
        finished = true;
        peakProgress = 1;
        stopSplashDrawing();
        onAssetsReady?.();
    }

    onLoading((progress) => {
        noteKaplayLoadProgress(progress);
        peakProgress = Math.max(peakProgress, getBootLoadProgress(), progress);
        if (splashDrawing) {
            drawSplashLoading(peakProgress);
        }
        finish();
    });

    onDraw(() => {
        if (!splashDrawing) return;
        // Keep painting while map.lua fetches (onLoading may be idle).
        peakProgress = Math.max(peakProgress, getBootLoadProgress());
        drawSplashLoading(peakProgress);
        finish();
    });

    onLoad(() => {
        finish();
    });
}
